import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { HelpText } from "@/components/HelpText";
import { setShowTips, useHelp, useShowTips } from "@/help";

/**
 * Instructions on or off: shows or hides the (i)s and tooltips, remembered on this device. Its
 * own tooltip shows on hover with a mouse, and for a few seconds after a tap (until a scroll or
 * a tap elsewhere), since a touch screen has no hover.
 */
export function TipsSwitch() {
  const on = useShowTips();
  const help = useHelp();
  const [flash, setFlash] = useState(false);

  const wrap = useRef<HTMLSpanElement>(null);

  // Gone after a few seconds, or straight away on a scroll or a tap or click anywhere else.
  useEffect(() => {
    if (!flash) return;
    const hide = () => setFlash(false);
    const onPointerDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) hide();
    };
    const timer = window.setTimeout(hide, 3500);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    window.addEventListener("keydown", hide, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("keydown", hide, true);
    };
  }, [flash]);

  return (
    <span ref={wrap} className={`tips-switch-wrap${flash ? " flash" : ""}`}>
      <button
        type="button"
        className={`tips-switch${on ? " on" : ""}`}
        aria-pressed={on}
        aria-describedby="tips-switch-tip"
        onClick={() => {
          setShowTips(!on);
          setFlash(true);
        }}
      >
        <Icon name="info" size={18} />
        <span>{on ? "Tips on" : "Tips off"}</span>
      </button>
      <span id="tips-switch-tip" role="tooltip" className="tips-switch-tip">
        <HelpText
          text={help("tipsSwitch")}
          lead={<strong>{on ? "Tips are on." : "Tips are off."}</strong>}
        />
      </span>
    </span>
  );
}
